import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import api from '../../utils/api';

const Topbar = ({ role }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  const showSearch = role === 'employee' || role === 'landlord';
  const searchEndpoint = role === 'landlord' ? '/landlord/search' : '/employee/search';

  useEffect(() => {
    if (!showSearch) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearch]);

  const handleSearch = (value) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) { setResults([]); setShowDropdown(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`${searchEndpoint}?q=${encodeURIComponent(value.trim())}`);
        setResults(res.data.results || []);
        setShowDropdown(true);
      } catch { setResults([]); }
    }, 300);
  };

  const handleLogout = () => {
    logout();
    navigate(role === 'landlord' ? '/landlord/login' : '/login');
  };

  const initials = user
    ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()
    : '?';

  return (
    <>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <header className="topbar" role="banner">
        <Link to={role === 'landlord' ? '/landlord/dashboard' : '/tenant/dashboard'} className="topbar-logo" aria-label="Affordable Telecom LLC — Go to dashboard">
          <div className="topbar-logo-dot" aria-hidden="true" />
          Affordable Telecom LLC
        </Link>

        <div className="topbar-right">
          {showSearch && (
            <div className="topbar-search-wrapper" ref={wrapperRef}>
              <input
                type="text"
                className="topbar-search-input"
                placeholder="Search tenants..."
                value={query}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => { if (results.length) setShowDropdown(true); }}
              />
              {showDropdown && results.length > 0 && (
                <div className="topbar-search-dropdown">
                  {results.map((r) => (
                    <button
                      key={r.id}
                      className="topbar-search-result"
                      onClick={() => { setSelected(r); setShowDropdown(false); }}
                    >
                      <span className="topbar-search-name">{r.first_name} {r.last_name}</span>
                      <span className="topbar-search-meta">
                        {r.unit_number ? `Unit ${r.unit_number}` : ''}{r.unit_number && r.property_name ? ' · ' : ''}{r.property_name || ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {showDropdown && results.length === 0 && query.trim() && (
                <div className="topbar-search-dropdown">
                  <div className="topbar-search-empty">No results found</div>
                </div>
              )}
            </div>
          )}

          {user && (
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
              {user.first_name} {user.last_name}
            </span>
          )}
          <button className="topbar-link" onClick={handleLogout}>Sign out</button>
          <div className="avatar" aria-hidden="true">{initials}</div>
        </div>
      </header>

      {selected && (
        <div className="topbar-modal-overlay" onClick={() => setSelected(null)}>
          <div className="topbar-modal" onClick={(e) => e.stopPropagation()}>
            <div className="topbar-modal-header">
              <h3>Tenant Details</h3>
              <button className="topbar-modal-close" onClick={() => setSelected(null)}>&times;</button>
            </div>
            <div className="topbar-modal-body">
              <div className="topbar-modal-row"><span className="topbar-modal-label">Name</span><span>{selected.first_name} {selected.last_name}</span></div>
              <div className="topbar-modal-row"><span className="topbar-modal-label">Email</span><span>{selected.email}</span></div>
              <div className="topbar-modal-row"><span className="topbar-modal-label">Phone</span><span>{selected.phone || '—'}</span></div>
              <div className="topbar-modal-row"><span className="topbar-modal-label">Unit</span><span>{selected.unit_number || '—'}</span></div>
              <div className="topbar-modal-row"><span className="topbar-modal-label">Property</span><span>{selected.property_name || '—'}</span></div>
              <div className="topbar-modal-row"><span className="topbar-modal-label">Status</span><span className={`topbar-modal-status topbar-modal-status--${selected.status}`}>{selected.status}</span></div>
              <div className="topbar-modal-row"><span className="topbar-modal-label">Lease Type</span><span>{selected.lease_type || '—'}</span></div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Topbar;
